import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import { artistCategoryOptions } from '@/lib/artistCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  title: '',
  category: '',
  category_other: '',
  art_form: '',
  art_form_other: '',
  nature_of_work: '',
  budget: '',
  state: '',
  city: '',
  custom_location: '',
  location: '',
  project_date: '',
  details: '',
  image_url: '',
};

const statusLabel = {
  pending_review: 'Admin review',
  published: 'Published',
  admin_rejected: 'Needs changes',
  closed: 'Closed',
  artist_accepted: 'Artist accepted',
  artist_declined: 'Declined',
  in_review: 'In review',
  poster_accepted: 'Selected',
  poster_rejected: 'Not selected',
};

const stateCityOptions = {
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Tirupati', 'Other'],
  Assam: ['Guwahati', 'Dibrugarh', 'Silchar', 'Tezpur', 'Other'],
  Bihar: ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Other'],
  Delhi: ['New Delhi', 'Dwarka', 'Rohini', 'Saket', 'Other'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Other'],
  Haryana: ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Other'],
  Jharkhand: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Other'],
  Karnataka: ['Bengaluru', 'Mysuru', 'Mangaluru', 'Hubballi', 'Other'],
  Kerala: ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur', 'Other'],
  Maharashtra: ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Other'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Other'],
  Odisha: ['Bhubaneswar', 'Cuttack', 'Puri', 'Rourkela', 'Other'],
  Punjab: ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Other'],
  Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Other'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Other'],
  Telangana: ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Other'],
  'Uttar Pradesh': ['Lucknow', 'Varanasi', 'Ghaziabad', 'Noida', 'Prayagraj', 'Kanpur', 'Mathura', 'Other'],
  Uttarakhand: ['Dehradun', 'Haridwar', 'Rishikesh', 'Haldwani', 'Other'],
  'West Bengal': ['Kolkata', 'Howrah', 'Siliguri', 'Durgapur', 'Other'],
};

const formatProjectDate = (value) => {
  if (!value) return { label: 'Date not set', passed: false };
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return { label: value, passed: false };
  const date = new Date(parsed);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    label: date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
    passed: date < today,
  };
};

const Projects = () => {
  const { artist, isAuthenticated: artistLoggedIn } = useArtistAuth();
  const { collaborator, isAuthenticated: collaboratorLoggedIn } = useCollaboratorAuth();
  const [projects, setProjects] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [responses, setResponses] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [message, setMessage] = useState('');
  const [busyAction, setBusyAction] = useState('');

  const loggedIn = artistLoggedIn || collaboratorLoggedIn;
  const activeName = collaboratorLoggedIn
    ? collaborator?.organization_name || collaborator?.name
    : artist?.name;

  const loadProjects = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const publicRequest = artistLoggedIn
        ? axios.get(`${API}/artist-auth/projects/opportunities`, { withCredentials: true })
        : axios.get(`${API}/projects`);
      const [publicResponse, mineResponse] = await Promise.all([
        publicRequest,
        loggedIn ? axios.get(`${API}/projects/mine`, { withCredentials: true }).catch(() => null) : Promise.resolve(null),
      ]);
      const prefResponse = loggedIn ? await axios.get(`${API}/projects/preferences`, { withCredentials: true }).catch(() => null) : null;
      setProjects(publicResponse.data.projects || []);
      setResponses([...(publicResponse.data.responses || []), ...(mineResponse?.data?.responses || [])]);
      setMyProjects(mineResponse?.data?.projects || []);
      setPreferences(prefResponse?.data || null);
    } catch (error) {
      setProjects([]);
      setMyProjects([]);
      setResponses([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistLoggedIn, collaboratorLoggedIn]);

  useEffect(() => {
    const intervalId = window.setInterval(() => loadProjects({ silent: true }), 25000);
    const handleFocus = () => loadProjects({ silent: true });
    window.addEventListener('focus', handleFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistLoggedIn, collaboratorLoggedIn]);

  const responseByProject = useMemo(() => {
    const map = {};
    responses.forEach((response) => {
      if (response?.project_id) map[response.project_id] = response;
    });
    return map;
  }, [responses]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const updateCategory = (value) => {
    setForm((current) => ({
      ...current,
      category: value,
      category_other: value === 'Other' ? current.category_other : '',
      art_form: '',
      art_form_other: '',
    }));
  };

  const updateArtForm = (value) => {
    setForm((current) => ({ ...current, art_form: value, art_form_other: value === 'Other' ? current.art_form_other : '' }));
  };

  const updateState = (value) => {
    setForm((current) => ({ ...current, state: value, city: '', custom_location: '', location: value }));
  };

  const updateCity = (value) => {
    setForm((current) => {
      const location = value && value !== 'Other' ? `${value}, ${current.state}` : current.state;
      return { ...current, city: value, custom_location: '', location };
    });
  };

  const updateCustomLocation = (value) => {
    setForm((current) => ({ ...current, custom_location: value, location: [value, current.state].filter(Boolean).join(', ') }));
  };

  const submitPayload = () => ({
    ...form,
    art_form: [
      form.category === 'Other' ? form.category_other : form.category,
      form.art_form === 'Other' ? form.art_form_other : form.art_form,
    ].filter(Boolean).join(' - '),
    budget: form.budget ? `\u20B9${form.budget}` : '',
    location: form.location || [form.city, form.state].filter(Boolean).join(', '),
  });

  const responsesForProject = (projectId) => responses.filter((response) => response.project_id === projectId);

  const updatePreference = async (field, value) => {
    const next = { ...(preferences || {}), [field]: value };
    setPreferences(next);
    try {
      const response = await axios.put(`${API}/projects/preferences`, { [field]: value }, { withCredentials: true });
      setPreferences(response.data);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to update project notification preference.');
    }
  };

  const emailProjectDetails = async (projectId) => {
    const actionKey = `email-${projectId}`;
    setBusyAction(actionKey);
    setMessage('');
    try {
      const response = await axios.post(`${API}/projects/${projectId}/email-details`, {}, { withCredentials: true });
      setMessage(response.data.message || 'Project job card details have been sent to your registered email.');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to email project details.');
    } finally {
      setBusyAction('');
    }
  };

  const uploadProjectImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setMessage('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/project-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      updateField('image_url', response.data.image_url || '');
      setMessage('Project image uploaded.');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to upload project image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const submitProject = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const response = await axios.post(`${API}/projects`, submitPayload(), { withCredentials: true });
      setForm(emptyForm);
      setMessage(response.data.message || 'Project submitted for admin review.');
      await loadProjects();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to submit project.');
    } finally {
      setSaving(false);
    }
  };

  const artistAction = async (projectId, action) => {
    const actionKey = `artist-${projectId}-${action}`;
    setBusyAction(actionKey);
    setMessage('');
    try {
      await axios.post(`${API}/artist-auth/projects/${projectId}/action`, { action }, { withCredentials: true });
      setMessage(action === 'undo' ? 'Action undone.' : action === 'accept' ? 'Project accepted. Poster will review your interest.' : 'Project declined. It will not show in your opportunity list.');
      await loadProjects();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to update response.');
    } finally {
      setBusyAction('');
    }
  };

  const posterAction = async (projectId, artistId, action) => {
    if (action === 'delete' && !window.confirm('Delete this artist response from your project response list?')) return;
    const actionKey = `poster-${projectId}-${artistId}-${action}`;
    setBusyAction(actionKey);
    setMessage('');
    try {
      await axios.post(`${API}/projects/${projectId}/poster-action`, { artist_id: artistId, action }, { withCredentials: true });
      setMessage(action === 'delete' ? 'Artist response deleted.' : action === 'undo' ? 'Artist response moved back to accepted interest.' : 'Artist response updated.');
      await loadProjects();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to update artist response.');
    } finally {
      setBusyAction('');
    }
  };

  const myProjectIds = useMemo(() => new Set(myProjects.map((project) => project.id).filter(Boolean)), [myProjects]);
  const posterResponses = responses.filter((response) => response.status !== 'artist_declined' && myProjectIds.has(response.project_id));

  return (
    <main className="min-h-screen bg-[#F8FAFC] pt-28 text-slate-950">
      <SEO
        path="/projects"
        title="KalaManch Projects and Work Opportunities"
        description="Post creative projects, review work opportunities, and connect artists with collaborators on KalaManch."
        keywords="artist projects India, creative work opportunities, hire artists, Kalamanch projects"
      />

      <section className="border-b border-orange-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-sm font-extrabold uppercase tracking-[0.35em] text-[#FF6F00]">Projects</p>
          <div className="mt-5 grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-end">
            <div>
              <h1 className="max-w-4xl text-4xl font-black leading-tight sm:text-5xl">Post work. Find artists. Review interest.</h1>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-700">
                Collaborators and fellow artists can post projects. KalaManch admin reviews each post before it appears for artists.
              </p>
            </div>
            <div className="rounded-[2rem] border border-emerald-100 bg-white p-5 shadow-sm">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-800">Workflow</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[
                  ['1', 'Post project', 'Add work details, budget, location, and image.'],
                  ['2', 'Admin approves', 'KalaManch reviews before public listing.'],
                  ['3', 'Artists respond', 'Artists accept or decline the opportunity.'],
                  ['4', 'Poster decides', 'Select the artist or reject the response.'],
                ].map(([step, title, copy]) => (
                  <div key={step} className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 to-white p-4">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FF6F00] text-sm font-black text-white">{step}</span>
                    <p className="mt-3 text-sm font-black text-slate-950">{title}</p>
                    <p className="mt-1 text-xs leading-5 text-slate-600">{copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
        <div className="space-y-6">
          <form onSubmit={submitProject} className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-black">Post a project</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {loggedIn ? `Posting as ${activeName || 'KalaManch user'}` : 'Login as an artist or collaborator to post a project. Public visitors can browse listings, but posting, details, and applying require login.'}
            </p>
            <div className="mt-5 grid gap-3">
              <input value={form.title} onChange={(event) => updateField('title', event.target.value)} placeholder="Project title / work name" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn} />
              <select value={form.category} onChange={(event) => updateCategory(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn}>
                <option value="">Select art category</option>
                {Object.keys(artistCategoryOptions).map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
              {form.category === 'Other' && (
                <input value={form.category_other} onChange={(event) => updateField('category_other', event.target.value)} placeholder="Write art category" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn} />
              )}
              <select value={form.art_form} onChange={(event) => updateArtForm(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn || !form.category}>
                <option value="">Select art form / subcategory</option>
                {(artistCategoryOptions[form.category] || []).map((subcategory) => <option key={subcategory} value={subcategory}>{subcategory}</option>)}
              </select>
              {form.art_form === 'Other' && (
                <input value={form.art_form_other} onChange={(event) => updateField('art_form_other', event.target.value)} placeholder="Write art form / speciality" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn} />
              )}
              <input value={form.nature_of_work} onChange={(event) => updateField('nature_of_work', event.target.value)} placeholder="Nature of work, e.g. wedding, reel, event, album" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" disabled={!loggedIn} />
              <label className="relative block">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base font-black text-slate-700">&#8377;</span>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={form.budget}
                  onChange={(event) => updateField('budget', event.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Fee / project budget"
                  className="w-full rounded-2xl border border-slate-200 py-3 pl-9 pr-4 text-slate-950"
                  required
                  disabled={!loggedIn}
                />
              </label>
              <select value={form.state} onChange={(event) => updateState(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn}>
                <option value="">Select state</option>
                {Object.keys(stateCityOptions).map((state) => <option key={state} value={state}>{state}</option>)}
              </select>
              <select value={form.city} onChange={(event) => updateCity(event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn || !form.state}>
                <option value="">Select city</option>
                {(stateCityOptions[form.state] || []).map((city) => <option key={city} value={city}>{city}</option>)}
              </select>
              {form.city === 'Other' && (
                <input value={form.custom_location} onChange={(event) => updateCustomLocation(event.target.value)} placeholder="Type city / village / venue" className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn} />
              )}
              <input type="date" value={form.project_date} onChange={(event) => updateField('project_date', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn} />
              <label className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/40 px-4 py-4 text-sm font-semibold text-slate-700">
                Project image
                <input type="file" accept="image/*" onChange={uploadProjectImage} className="mt-3 block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-white file:px-4 file:py-2 file:font-bold file:text-[#FF6F00]" disabled={!loggedIn || uploadingImage} />
                <span className="mt-2 block text-xs font-medium text-slate-500">{uploadingImage ? 'Uploading image...' : 'Upload from PC or mobile. JPG, PNG, or WebP.'}</span>
              </label>
              {form.image_url && (
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                  <img src={form.image_url} alt="Project preview" className="h-32 w-full rounded-xl object-cover" />
                  <button type="button" onClick={() => updateField('image_url', '')} className="mt-2 text-xs font-bold text-red-600">Remove image</button>
                </div>
              )}
              <textarea value={form.details} onChange={(event) => updateField('details', event.target.value)} placeholder="Project detail, expectation, timeline, contact process" className="min-h-[140px] rounded-2xl border border-slate-200 px-4 py-3 text-slate-950" required disabled={!loggedIn} />
            </div>
            <button type="submit" disabled={!loggedIn || saving} className="mt-5 rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-orange-100 disabled:bg-slate-300">
              {saving ? 'Submitting...' : 'Submit for Admin Review'}
            </button>
            {message && <p className="mt-4 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm font-semibold text-slate-700">{message}</p>}
          </form>

          {loggedIn && preferences && (
            <section className="rounded-[2rem] border border-purple-100 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-black">Project notifications</h2>
              <p className="mt-2 text-sm text-slate-600">Choose how KalaManch should alert you about new and awarded projects.</p>
              <div className="mt-4 grid gap-3">
                {[
                  ['project_posted_email_opt_in', 'Email me when a new project is listed'],
                  ['project_posted_push_opt_in', 'Notify me in app/website for new projects'],
                  ['project_awarded_email_opt_in', 'Email me when a project is awarded/selected'],
                  ['project_awarded_push_opt_in', 'Notify me in app/website when selected'],
                ].map(([field, label]) => (
                  <label key={field} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-800">
                    <span>{label}</span>
                    <input type="checkbox" checked={Boolean(preferences[field])} onChange={(event) => updatePreference(field, event.target.checked)} className="h-5 w-5 accent-[#FF6F00]" />
                  </label>
                ))}
              </div>
            </section>
          )}

          {myProjects.length > 0 && (
            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-black">Projects I posted</h2>
              <div className="mt-4 grid gap-4">
                {myProjects.map((project) => {
                  const dateState = formatProjectDate(project.project_date);
                  const projectResponses = responsesForProject(project.id);
                  const accepted = projectResponses.filter((response) => ['artist_accepted', 'in_review', 'poster_accepted'].includes(response.status));
                  return (
                    <div key={project.id} className="rounded-3xl border border-slate-100 bg-slate-50 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-black text-slate-950">{project.title}</p>
                          <p className="mt-1 text-sm text-slate-600">{project.art_form} {project.location ? `| ${project.location}` : ''}</p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-extrabold text-[#4A148C]">{statusLabel[project.review_status] || project.review_status}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                        <span className={`rounded-full px-3 py-1 ${dateState.passed ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'}`}>{dateState.passed ? 'Date passed' : 'Date'}: {dateState.label}</span>
                        <span className="rounded-full bg-white px-3 py-1 text-slate-700">{projectResponses.length} response(s)</span>
                        <span className="rounded-full bg-white px-3 py-1 text-slate-700">{accepted.length} active response(s)</span>
                      </div>
                      {accepted.length > 0 && (
                        <div className="mt-3 rounded-2xl bg-white p-3">
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Accepted by</p>
                          <p className="mt-1 text-sm font-semibold text-slate-800">{accepted.map((response) => response.artist_name).join(', ')}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="space-y-6">
          <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Job board</p>
                <h2 className="mt-1 text-2xl font-black">Open work opportunities</h2>
                <p className="mt-2 text-sm text-slate-600">Review budgets, location, date, and artist-fit before responding.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-black text-slate-700">{projects.length} listed</span>
            </div>
            {loading ? (
              <p className="mt-5 text-slate-500">Loading projects...</p>
            ) : projects.length === 0 ? (
              <div className="mt-5 rounded-[1.5rem] border border-dashed border-orange-200 bg-gradient-to-br from-orange-50 to-white p-8 text-center">
                <p className="text-xl font-black text-slate-950">No approved projects yet.</p>
                <p className="mt-2 text-sm text-slate-600">Once admin approves a project, artists will see it here like a job board.</p>
              </div>
            ) : (
              <div className="mt-5 grid gap-4">
                {projects.map((project) => {
                  const response = responseByProject[project.id];
                  const dateState = formatProjectDate(project.project_date);
                  return (
                    <article key={project.id} className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl">
                      <div className="grid md:grid-cols-[180px_1fr]">
                        <div className="bg-slate-100">
                          {project.image_url ? (
                            <img src={project.image_url} alt={project.title} className="h-full min-h-44 w-full object-cover" />
                          ) : (
                            <div className="flex h-full min-h-44 items-center justify-center bg-gradient-to-br from-[#4A148C] to-[#FF6F00] p-5 text-center text-sm font-black uppercase tracking-[0.16em] text-white">KalaManch Project</div>
                          )}
                        </div>
                        <div className="p-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h3 className="text-xl font-black">{project.title}</h3>
                            <p className="mt-1 text-sm font-semibold text-[#4A148C]">{project.art_form} opportunity</p>
                          </div>
                          {project.is_featured && <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-extrabold text-[#C45A00]">Featured</span>}
                        </div>
                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-700">
                          {loggedIn ? project.details : 'Login to view full project details, expectations, and response options.'}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-700">
                          {project.nature_of_work && <span className="rounded-full bg-slate-100 px-3 py-1">{project.nature_of_work}</span>}
                          {project.budget && <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-800">{loggedIn ? project.budget : 'Budget visible after login'}</span>}
                          {project.location && <span className="rounded-full bg-purple-50 px-3 py-1 text-[#4A148C]">{loggedIn ? project.location : 'Location after login'}</span>}
                          <span className={`rounded-full px-3 py-1 ${dateState.passed ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-800'}`}>{dateState.passed ? 'Date passed' : 'Date'}: {dateState.label}</span>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Posted by {project.poster_name}</p>
                          {loggedIn && (
                            <button
                              type="button"
                              onClick={() => emailProjectDetails(project.id)}
                              disabled={busyAction === `email-${project.id}`}
                              className="rounded-full border border-orange-200 px-4 py-2 text-xs font-black text-[#C45A00] transition active:scale-95 hover:bg-orange-50 disabled:opacity-60"
                            >
                              {busyAction === `email-${project.id}` ? 'Sending...' : 'Email me details'}
                            </button>
                          )}
                        </div>
                        {!loggedIn && (
                          <div className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 p-4">
                            <p className="text-sm font-bold text-slate-800">Login to view full job card details and apply.</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <a href="/artists/login" className="rounded-full bg-[#FF6F00] px-4 py-2 text-xs font-black text-white">Artist Login</a>
                              <a href="/collaborators/login" className="rounded-full border border-[#4A148C] px-4 py-2 text-xs font-black text-[#4A148C]">Collaborator Login</a>
                            </div>
                          </div>
                        )}
                        {artistLoggedIn && (
                          <div className="mt-5 flex flex-wrap gap-3">
                            {response ? (
                              <>
                                <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700">{statusLabel[response.status] || response.status}</span>
                                <button type="button" onClick={() => artistAction(project.id, 'undo')} disabled={busyAction === `artist-${project.id}-undo`} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-extrabold text-slate-700 transition active:scale-95 hover:bg-slate-50 disabled:opacity-60">{busyAction === `artist-${project.id}-undo` ? 'Undoing...' : 'Undo'}</button>
                              </>
                            ) : (
                              <>
                                <button type="button" onClick={() => artistAction(project.id, 'accept')} disabled={busyAction === `artist-${project.id}-accept`} className="rounded-full bg-[#FF6F00] px-5 py-2 text-sm font-extrabold text-white transition active:scale-95 disabled:opacity-60">{busyAction === `artist-${project.id}-accept` ? 'Accepting...' : 'Accept Interest'}</button>
                                <button type="button" onClick={() => artistAction(project.id, 'decline')} disabled={busyAction === `artist-${project.id}-decline`} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-extrabold text-slate-700 transition active:scale-95 hover:bg-slate-50 disabled:opacity-60">{busyAction === `artist-${project.id}-decline` ? 'Declining...' : 'Decline'}</button>
                              </>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {posterResponses.length > 0 && (
            <section className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-black">Artist responses</h2>
              <p className="mt-2 text-sm text-slate-600">Only responses to projects you posted appear here. Selection controls are poster-side only.</p>
              <div className="mt-4 space-y-3">
                {posterResponses.map((response) => (
                  <div key={response.id || `${response.project_id}-${response.artist_id}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-black text-slate-950">{response.artist_name}</p>
                        <p className="text-sm text-slate-600">{response.project_title || response.project?.title}</p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-extrabold text-[#4A148C]">{statusLabel[response.status] || response.status}</span>
                    </div>
                    {response.status !== 'artist_declined' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" onClick={() => posterAction(response.project_id, response.artist_id, 'accept')} disabled={busyAction === `poster-${response.project_id}-${response.artist_id}-accept`} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-extrabold text-white transition active:scale-95 disabled:opacity-60">{busyAction === `poster-${response.project_id}-${response.artist_id}-accept` ? 'Selecting...' : 'Select Artist'}</button>
                        <button type="button" onClick={() => posterAction(response.project_id, response.artist_id, 'reject')} disabled={busyAction === `poster-${response.project_id}-${response.artist_id}-reject`} className="rounded-full border border-red-200 px-4 py-2 text-xs font-extrabold text-red-600 transition active:scale-95 disabled:opacity-60">{busyAction === `poster-${response.project_id}-${response.artist_id}-reject` ? 'Rejecting...' : 'Reject'}</button>
                        <button type="button" onClick={() => posterAction(response.project_id, response.artist_id, 'undo')} disabled={busyAction === `poster-${response.project_id}-${response.artist_id}-undo`} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-extrabold text-slate-600 transition active:scale-95 disabled:opacity-60">{busyAction === `poster-${response.project_id}-${response.artist_id}-undo` ? 'Undoing...' : 'Undo'}</button>
                        <button type="button" onClick={() => posterAction(response.project_id, response.artist_id, 'delete')} disabled={busyAction === `poster-${response.project_id}-${response.artist_id}-delete`} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-extrabold text-slate-500 transition active:scale-95 hover:bg-white disabled:opacity-60">{busyAction === `poster-${response.project_id}-${response.artist_id}-delete` ? 'Deleting...' : 'Delete'}</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </section>
    </main>
  );
};

export default Projects;

